/**
 * Utility functions for image compression and processing
 */
import imageCompression from 'browser-image-compression';

export interface CompressionOptions {
  maxWidth?: number;
  maxHeight?: number;
  quality?: number;
  maxSizeKB?: number;
}

export type ImagePreset = 
  | 'profile-photo'      // Small avatars, circular display
  | 'line-photo'         // Border crossing photos, needs detail
  | 'line-photo-preview' // Quick preview thumbnails
  | 'ad-banner'          // Marketing images, professional quality
  | 'ad-thumbnail'       // Small ad previews in lists
  | 'general';           // Fallback for other uses

export interface PresetConfig {
  maxWidth: number;
  maxHeight: number;
  quality: number;
  maxSizeKB: number;
  description: string;
}

export const COMPRESSION_PRESETS: Record<ImagePreset, PresetConfig> = {
  'profile-photo': {
    maxWidth: 400,
    maxHeight: 400,
    quality: 0.85,
    maxSizeKB: 100,
    description: 'Small avatar images, circular display'
  },
  'line-photo': {
    maxWidth: 1920,
    maxHeight: 1080,
    quality: 0.8,
    maxSizeKB: 500,
    description: 'Full-size border crossing photos with detail'
  },
  'line-photo-preview': {
    maxWidth: 640,
    maxHeight: 480,
    quality: 0.75,
    maxSizeKB: 150,
    description: 'Quick-loading thumbnail previews'
  },
  'ad-banner': {
    maxWidth: 1200,
    maxHeight: 600,
    quality: 0.88,
    maxSizeKB: 350,
    description: 'Professional ad images, high quality'
  },
  'ad-thumbnail': {
    maxWidth: 300,
    maxHeight: 300,
    quality: 0.8,
    maxSizeKB: 80,
    description: 'Small ad preview images'
  },
  'general': {
    maxWidth: 1920,
    maxHeight: 1080,
    quality: 0.8,
    maxSizeKB: 500,
    description: 'General purpose compression'
  }
};

/**
 * Compresses an image file with EXIF orientation correction
 */
export const compressImage = async (
  file: File | Blob,
  preset: ImagePreset = 'general',
  customOptions?: Partial<CompressionOptions>,
  qualityMultipliers?: { qualityMultiplier: number; sizeMultiplier: number }
): Promise<Blob> => {
  const presetConfig = COMPRESSION_PRESETS[preset];
  
  // Apply quality multipliers if provided
  const qualityMult = qualityMultipliers?.qualityMultiplier ?? 1.0;
  const sizeMult = qualityMultipliers?.sizeMultiplier ?? 1.0;
  
  const options: CompressionOptions = {
    maxWidth: customOptions?.maxWidth ?? presetConfig.maxWidth,
    maxHeight: customOptions?.maxHeight ?? presetConfig.maxHeight,
    quality: Math.min(1.0, (customOptions?.quality ?? presetConfig.quality) * qualityMult),
    maxSizeKB: Math.round((customOptions?.maxSizeKB ?? presetConfig.maxSizeKB) * sizeMult),
  };

  try {
    // Convert Blob to File if needed for browser-image-compression
    let fileToProcess = file;
    if (!(file instanceof File)) {
      fileToProcess = new File([file], 'image.jpg', { type: file.type || 'image/jpeg' });
    }

    console.log(`Compressing image with preset: ${preset}`, {
      originalSize: (file.size / 1024).toFixed(2) + 'KB',
      targetSize: options.maxSizeKB + 'KB'
    });

    // Use browser-image-compression which handles EXIF orientation automatically
    const compressedFile = await imageCompression(fileToProcess as File, {
      maxWidthOrHeight: Math.max(options.maxWidth, options.maxHeight),
      initialQuality: options.quality,
      maxSizeMB: options.maxSizeKB / 1024, // Convert KB to MB
      useWebWorker: true,
      exifOrientation: 1, // Normalize orientation
    });

    console.log(`Compression complete:`, {
      preset,
      originalSize: (file.size / 1024).toFixed(2) + 'KB',
      compressedSize: (compressedFile.size / 1024).toFixed(2) + 'KB',
      reduction: (((file.size - compressedFile.size) / file.size) * 100).toFixed(1) + '%'
    });

    return compressedFile;
  } catch (error) {
    console.error('Error compressing image:', error);
    throw new Error('Failed to compress image');
  }
};

/**
 * Get information about a compression preset
 */
export const getPresetInfo = (preset: ImagePreset): PresetConfig => {
  return COMPRESSION_PRESETS[preset];
};

/**
 * Get all available presets
 */
export const getAvailablePresets = (): ImagePreset[] => {
  return Object.keys(COMPRESSION_PRESETS) as ImagePreset[];
};

/**
 * Estimate compressed size (rough estimate)
 */
export const estimateCompressedSize = (
  originalSizeBytes: number,
  preset: ImagePreset
): { min: number; max: number; target: number } => {
  const config = COMPRESSION_PRESETS[preset];
  return {
    min: Math.min(originalSizeBytes * 0.1, config.maxSizeKB * 1024 * 0.8),
    max: config.maxSizeKB * 1024,
    target: config.maxSizeKB * 1024 * 0.9
  };
};

/**
 * Converts a blob to base64 data URL
 */
export const blobToDataURL = (blob: Blob): Promise<string> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });
};

/**
 * Creates a filename with user ID and timestamp
 */
export const createPhotoFilename = (userId: string): string => {
  const timestamp = new Date().toISOString().replace(/[:.-]/g, '');
  return `${userId}_${timestamp}.jpg`;
};